/**
 * The one tag→message map of the Compare surfaces form (spec §4.2): every tag
 * `compareSchema` can raise (`COMPARE_VALIDATION_TAGS`) has its text here. The
 * dialog hands it to `FormMessageText`, so every `FormMessage` under the form
 * — its own fields and the shared `SurfacePicker` / `FormInputRoleField` —
 * prints text, never a raw tag. A message that is not a tag (already text)
 * passes through unchanged.
 */
import { useTranslation } from 'react-i18next';

import type { CompareKind } from '@/lib/analyses/compare/availability';
import { parseValidationTag, type CompareValidationTag } from '@/lib/analyses/compare/formSchema';
import {
  MAX_CROSSFALL_TOLERANCE_PCT,
  MIN_CROSSFALL_BAND_WIDTH_M,
  MIN_CROSSFALL_TOLERANCE_PCT,
} from '@/lib/stationVolumes/dialogModel';

/** `kind`: the compare mode, which names the two inputs of `sameLayer`. */
export function useCompareValidationText(kind: CompareKind = 'design'): (message: string) => string {
  const { t, i18n } = useTranslation('project');
  const fmt = (n: number) => new Intl.NumberFormat(i18n.language).format(n);
  // `Record<CompareValidationTag, …>`: a tag added to the schema without text
  // here is a type error, not a raw tag on screen.
  const messages: Record<CompareValidationTag, (bounds: { min?: number; max?: number }) => string> = {
    noResult: () => t('compareSurfaces.results.noResult'),
    layerRequired: () => t('compareSurfaces.errors.layerRequired'),
    sameLayer: () =>
      kind === 'survey' ? t('compareSurfaces.errors.sameLayerSurvey') : t('compareSurfaces.errors.sameLayer'),
    minMustBeLessThanMax: () => t('compareSurfaces.errors.minMustBeLessThanMax'),
    deviationNeedsDesign: () => t('compareSurfaces.reason.deviationNeedsDesign'),
    mustBePositive: () => t('compareSurfaces.errors.mustBePositive'),
    mustBeInteger: () => t('compareSurfaces.errors.mustBeInteger'),
    // The bounds come with the tag (`outOfRangeTag`); a lower bound alone
    // reads "… or more".
    outOfRange: ({ min, max }) =>
      max === undefined && min !== undefined
        ? t('compareSurfaces.errors.atLeast', { min: fmt(min) })
        : t('compareSurfaces.errors.outOfRange', {
            min: min === undefined ? '…' : fmt(min),
            max: max === undefined ? '…' : fmt(max),
          }),
    alignmentRequired: () => t('stationVolumes.dialog.alignmentRequired'),
    swathZero: () => t('stationVolumes.dialog.swathZero'),
    intervalRange: () => t('stationVolumes.errors.intervalRange'),
    swathRange: () => t('stationVolumes.errors.swathRange'),
    offsetBinRange: () => t('stationVolumes.errors.offsetBinRange'),
    crossfallBand: () => t('stationVolumes.errors.crossfallBandRange', { min: MIN_CROSSFALL_BAND_WIDTH_M }),
    crossfallWidth: () => t('stationVolumes.errors.crossfallBandRange', { min: MIN_CROSSFALL_BAND_WIDTH_M }),
    crossfallSwath: () => t('stationVolumes.errors.crossfallSwath'),
    crossfallNoBand: () => t('stationVolumes.errors.crossfallNoBand'),
    crossfallTolerance: () =>
      t('stationVolumes.errors.crossfallTolerance', {
        min: MIN_CROSSFALL_TOLERANCE_PCT,
        max: MAX_CROSSFALL_TOLERANCE_PCT,
      }),
    thicknessRequired: () => t('expectation.errors.thicknessRequired'),
    toleranceRequired: () => t('expectation.errors.toleranceRequired'),
    surfaceRequired: () => t('expectation.errors.surfaceRequired'),
    sameSurface: () => t('expectation.errors.sameSurface'),
    toleranceNegative: () => t('expectation.errors.toleranceNegative'),
    failAreaPctRange: () => t('expectation.errors.failAreaPctRange'),
    stationVolumesRequiredForExpectation: () => t('expectation.errors.stationVolumesRequiredForExpectation'),
  };
  return (message: string) => {
    const { tag, min, max } = parseValidationTag(message);
    return Object.hasOwn(messages, tag) ? messages[tag as CompareValidationTag]({ min, max }) : message;
  };
}
